import React, {useMemo} from 'react';
import type {CurrencyColumn, DataGridColumn, DataGridServerProps, TextColumn} from '@igp/shared/components/data-grid';
import {DataGridServer} from '@igp/shared/components/data-grid';
import {formatTimestamp} from '@igp/shared/utils';

import {localizedHeaders} from '../hooks';
import type {DebtTransferLogViewModel, DebtTransferLogViewModelKeys} from '../types';

function useDebtTransferLogColumns(columns: DebtTransferLogViewModelKeys[]): DataGridColumn<DebtTransferLogViewModel>[] {
    const currency = 'USD';
    const defaultColumn: Partial<DataGridColumn<DebtTransferLogViewModel>> = {
        sortable: true,
        width: 150,
    };
    const logId: TextColumn<DebtTransferLogViewModel> = {
        ...defaultColumn,
        field: 'logId',
        type: 'text',
        valueGetter: model => model?.logId,
        title: localizedHeaders.logId,
    };
    const transferDate: TextColumn<DebtTransferLogViewModel> = {
        ...defaultColumn,
        field: 'transferDate',
        type: 'text',
        valueGetter: model => formatTimestamp(model.transferDate, 'date'),
        title: localizedHeaders.transferDate,
    };
    const masterAgentId: TextColumn<DebtTransferLogViewModel> = {
        ...defaultColumn,
        field: 'masterAgentId',
        type: 'text',
        valueGetter: model => model.masterAgentId,
        title: localizedHeaders.masterAgentId,
    };
    const masterAgentUsername: TextColumn<DebtTransferLogViewModel> = {
        ...defaultColumn,
        field: 'masterAgentUsername',
        type: 'text',
        valueGetter: model => model.masterAgentUsername,
        title: localizedHeaders.masterAgentUsername,
        width: 250,
    };
    const agentId: TextColumn<DebtTransferLogViewModel> = {
        ...defaultColumn,
        field: 'agentId',
        type: 'text',
        valueGetter: model => model.agentId,
        title: localizedHeaders.agentId,
    };
    const agentUsername: TextColumn<DebtTransferLogViewModel> = {
        ...defaultColumn,
        field: 'agentUsername',
        type: 'text',
        valueGetter: model => model.agentUsername,
        title: localizedHeaders.agentUsername,
        width: 200,
    };
    const transferredAmount: CurrencyColumn<DebtTransferLogViewModel> = {
        ...defaultColumn,
        field: 'transferredAmount',
        type: 'currency',
        valueGetter: model => ({
            amount: model.transferredAmount,
            currency,
        }),
        title: localizedHeaders.transferredAmount,
        width: 200,
    };
    const createdAt: TextColumn<DebtTransferLogViewModel> = {
        ...defaultColumn,
        field: 'createdAt',
        type: 'text',
        valueGetter: model => formatTimestamp(model.createdAt, 'date'),
        title: localizedHeaders.createdAt,
    };

    const config: Record<DebtTransferLogViewModelKeys, DataGridColumn<DebtTransferLogViewModel>> = {
        logId,
        transferDate,
        masterAgentId,
        masterAgentUsername,
        agentId,
        agentUsername,
        transferredAmount,
        createdAt,
    };
    return useMemo(() => columns.map(column => config[column]), [columns.join(',')]);
}

type DebtTransferLogDataGridProps = Omit<DataGridServerProps<DebtTransferLogViewModel, DebtTransferLogViewModelKeys>, 'columns'> & {
    columns: DebtTransferLogViewModelKeys[];
};

export function DebtTransferLogDataGrid({columns, ...props}: DebtTransferLogDataGridProps) {
    const columnsConfiguration = useDebtTransferLogColumns(columns);
    return <DataGridServer columns={columnsConfiguration} {...props} />;
}
